import {ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, TemplateRef} from '@angular/core';
import {Popover, PopoverRef, Position} from '@greetgo/popover';
import {Observable, of, Subject} from 'rxjs';
import {DYNAMIC_FORMS_SERVICE, DynamicFormsService} from '../DynamicFormsService';
import {BusinessFieldViewType} from '../../model/BusinessFieldViewType';
import {BusinessFieldRef} from '../../model/BusinessFieldRef';
import {BoInstanceSelectorRecord} from '../../model/BoInstanceSelectorRecord';
import {catchError, debounceTime, filter, map, startWith, switchMap, tap} from 'rxjs/operators';
import {BoInstanceSelectorFilter} from '../../model/BoInstanceSelectorFilter';
import {BoInstanceSelectorRequest} from '../../model/BoInstanceSelectorRequest';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {byOrderIndexField, DEBOUNCE_TIME, logAndReturnEmpty, safeUnsub} from '../Util';

@Component({
  selector: 'app-multiple',
  templateUrl: './multiple.component.html',
  styleUrls: ['./multiple.component.scss']
})
export class MultipleComponent implements OnInit {
  @Input() type: BusinessFieldViewType;
  @Input() businessFields: BusinessFieldRef[];
  @Input() businessObjectId: string;
  @Input() chosenIds: string[];
  @Input() isEditable: boolean;
  @Output() boInstanceAdded = new EventEmitter<BoInstanceSelectorRecord>();
  @Output() boInstanceDeleted = new EventEmitter<BoInstanceSelectorRecord>();
  private popoverRef: PopoverRef;
  readonly searchValueChanged = new Subject<string>();
  isAllChosen = false;
  popoverOpened = false;
  allBoInstanceIds: string[] = [];
  chosenRecords: BoInstanceSelectorRecord[];
  searchRecords: BoInstanceSelectorRecord[];

  constructor(
    @Inject(DYNAMIC_FORMS_SERVICE)
    private readonly service: DynamicFormsService,
    private readonly popover: Popover,
    private readonly cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.loadByIds().subscribe();
  }

  values(values: string[]) {
    if (!values || values.length === 0) { return ''; }
    return values.reduce((a, b) => a + ' ' + b);
  }

  showPopover(origin: HTMLDivElement, content: TemplateRef<any>) {

    const component = this;
    const position = Position.right;
    const color = 'white';
    const hasBackdrop = true;

    this.service.loadAllBoInstanceIds(this.businessObjectId).pipe(
      tap(setAllBoInstanceIds),
    ).subscribe();

    const subscription = this.loadSearchRecords().pipe(
      filter(isPopoverClosed),
      tap(openPopover)
    ).subscribe();

    function setAllBoInstanceIds(ids: string[]) {

      component.allBoInstanceIds = ids;
      component.updateIsAllChosen();

    }

    function isPopoverClosed() {

      return !component.popoverOpened;

    }

    function openPopover() {

      component.popoverOpened = true;
      component.popoverRef = component.popover.open({origin, content, position, color, hasBackdrop});
      component.popoverRef.beforeClosed.subscribe(() => {
        safeUnsub(subscription);
        component.popoverOpened = false;
      });

    }

  }

  private loadSearchRecords(): Observable<BoInstanceSelectorRecord[]> {
    const component = this;

    return this.searchValueChanged.asObservable()
      .pipe(
        debounceTime(DEBOUNCE_TIME),
        startWith<string, string>(undefined),
        map(asFilter),
        switchMap(loadBoInstanceRecords),
        tap(setSearchRecords),
      );

    function asFilter(searchValue: string) {

      return {
        search: searchValue,
        businessObjectId: component.businessObjectId,
        fieldIds: component.businessFields.filter(z => z.toShow).sort(byOrderIndexField).map(z => z.fieldId),
      };

    }

    function loadBoInstanceRecords(search: BoInstanceSelectorFilter) {

      return component.service.loadBoInstanceRecords(search).pipe(catchError(logAndReturnEmpty));

    }

    function setSearchRecords(recs: BoInstanceSelectorRecord[]) {

      component.searchRecords = recs;
      component.searchRecords.forEach(x => x.checked = component.chosenIds.includes(x.boInstanceId));

    }

  }

  private loadByIds(): Observable<BoInstanceSelectorRecord[]> {
    const component = this;
    const request = this.asRequest(this.chosenIds);
    const source = this.chosenIds && this.chosenIds.length > 0
      ? this.service.loadBoInstanceRecordsByIds(request)
      : of([]);

    return source.pipe(
      tap(setChosenRecords)
    );

    function setChosenRecords(res: BoInstanceSelectorRecord[]) {

      component.chosenRecords = res;
      component.cdr.detectChanges();

    }

  }

  private asRequest(chosenIds: string[]) {
    return {
      boInstanceIds: chosenIds,
      businessObjectId: this.businessObjectId,
      fieldIds: this.businessFields
        .filter(z => z.toShow)
        .sort(byOrderIndexField)
        .map(z => z.fieldId),
    } as BoInstanceSelectorRequest;
  }

  toggleBoInstance(record: BoInstanceSelectorRecord, change: MatCheckboxChange) {
    record.checked = change.checked;
    if (change.checked) {
      this.addBoInstance(record);
    } else {
      this.deleteBoInstance(record);
    }
    this.updateIsAllChosen();
  }

  selectBoInstance(rec: BoInstanceSelectorRecord) {
    this.chosenRecords.forEach(x => {
      x.checked = false;
      this.boInstanceDeleted.emit(x);
    });
    this.boInstanceAdded.emit(rec);
    this.chosenRecords = [rec];
    rec.checked = true;
  }

  private addBoInstance(record: BoInstanceSelectorRecord) {
    this.boInstanceAdded.emit(record);
    if (this.isSingle()) {
      this.chosenRecords = [record];
      this.searchRecords.forEach(x => x.checked = x.boInstanceId === record.boInstanceId);
      return;
    }
    this.chosenRecords.push(record);
  }

  deleteBoInstance(record: BoInstanceSelectorRecord) {
    this.boInstanceDeleted.emit(record);
    if (this.isSingle()) {
      this.chosenRecords = [];
      return;
    }
    this.chosenRecords = this.chosenRecords.filter(x => x.boInstanceId !== record.boInstanceId);
  }

  private isSingle() {
    return this.type === BusinessFieldViewType.SINGLE;
  }

  isMultiple() {
    return this.type === BusinessFieldViewType.MULTIPLE;
  }

  isStrArraysEqual(array1: string[], array2: string[]): boolean {
    if (array1.length !== array2.length) {
      return false;
    }
    array1.sort();
    array2.sort();
    return array1.every((value, index) => value === array2[index]);
  }

  updateIsAllChosen() {

    this.isAllChosen = this.isStrArraysEqual(this.allBoInstanceIds, this.chosenRecords.map(x => x.boInstanceId));

  }

  toggleAllChosen(change: MatCheckboxChange) {
    const component = this;

    this.chosenRecords.forEach(rec => this.boInstanceDeleted.emit(rec));

    if (change.checked) {
      this.chosenIds = this.allBoInstanceIds;
      this.searchRecords.forEach(x => x.checked = true);
    } else {
      this.chosenIds = [];
      this.searchRecords.forEach(x => x.checked = false);
    }


    this.loadByIds().subscribe(addBoInstance);

    function addBoInstance(recs): void {

      recs.forEach(rec => component.boInstanceAdded.emit(rec));

    }
  }
}
